/* DecantGems cart page upsells (blocks/_dg-cart-upsells.liquid, owner 16 Oct 2026).
   - One-click add: every "+ Add" is a real form (POST /cart/add lands on /cart without JavaScript). Here it becomes
     /cart/add.js with the cart section in `sections`, a short "Added" state on the button, then Horizon's standard
     cart:lines-update event: cart-items-component morphs the whole cart section (lines, summary, free-shipping line and
     these rails, which leave the new line out) and the header count follows. No page reload.
   - A refusal (sold out, stock limit) is shown on the card itself (Shopify's own sentence, else ours).
   - "Recently viewed": handles from the dg:recent storage (dg.js), cards from /products/<handle>?section_id=dg-cart-card
     (prints only in-stock buyable samples), without the cart lines and the cards already shown above. Kept in memory,
     so a section re-render refills the rail without new requests.
   - Rails: prev/next buttons scroll by one view (event delegation: they survive the re-render). */
import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  'use strict';
  var RECENT_KEY = 'dg:recent';
  var cache = {}; /* handle -> card html ('' = not shown) */
  var pending = {};

  function root() { return document.querySelector('[data-dg-cu]'); }
  function txt(key, fallback) { var r = root(); return (r && r.getAttribute('data-t-' + key)) || fallback || ''; }
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (c) { if (c.dataset.sectionId) ids.push(c.dataset.sectionId); });
    return ids;
  }
  var live;
  function announce(message) {
    if (!live) {
      live = document.createElement('p');
      live.className = 'dgcu-live';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = '';
    window.setTimeout(function () { live.textContent = message; }, 30);
  }

  function itemsOf(form) {
    if (form.hasAttribute('data-dg-cu-set')) {
      return (form.getAttribute('data-items') || '').split(',').filter(Boolean).map(function (id) { return { id: parseInt(id, 10), quantity: 1 }; });
    }
    var id = form.querySelector('input[name="id"]');
    return id ? [{ id: parseInt(id.value, 10), quantity: 1 }] : [];
  }

  function add(form, event) {
    var button = form.querySelector('button[type="submit"]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    var items = itemsOf(form);
    if (!items.length) return;
    var holder = form.closest('.dgcu-card, .dgcu-set');
    var msg = holder && holder.querySelector('.dgcu-card__msg');
    if (msg) { msg.hidden = true; msg.textContent = ''; }
    var label = button.querySelector('[data-dg-cu-label]');
    var before = label ? label.textContent : '';
    var list = holder && holder.getAttribute('data-list');
    var railIndex = -1;
    if (holder && holder.parentNode) railIndex = Array.prototype.indexOf.call(holder.parentNode.children, holder);
    /* aria-busy guards a second click; the button is not disabled, so keyboard focus stays on it */
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = txt('adding', before);
    var ids = cartSectionIds();
    var body = { items: items };
    if (ids.length) { body.sections = ids.join(','); body.sections_url = window.location.pathname; }
    function restore() {
      button.removeAttribute('aria-busy');
      button.disabled = false;
      if (label) label.textContent = before;
    }
    function fail(text) {
      restore();
      if (msg) { msg.textContent = text; msg.hidden = false; }
      else announce(text);
    }
    var added = null;
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (j) {
          if (!r.ok || j.status) {
            var e = new Error(j.description || j.message || ('HTTP ' + r.status));
            e.shop = !!(j.description || j.message);
            e.code = r.status;
            throw e;
          }
          return j;
        });
      })
      .then(function (res) {
        added = res;
        button.classList.add('is-added');
        if (label) label.textContent = txt('added', before);
        announce(txt('status', '').replace('__NAME__', button.getAttribute('data-name') || ''));
        return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        /* the "Added" state stays a moment, then the cart section re-renders (the card leaves the rail) */
        return new Promise(function (resolve) { window.setTimeout(resolve, 650); }).then(function () {
          var deferred = CartLinesUpdateEvent.createPromise();
          form.dispatchEvent(new CartLinesUpdateEvent({
            action: 'add',
            context: 'cart',
            lines: items.map(function (i) { return { merchandiseId: String(i.id), quantity: i.quantity }; }),
            promise: deferred.promise
          }));
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, itemCount: cart.item_count, source: 'dg-cart', sections: added && added.sections, didError: false }
          });
          keepFocus(button, list, railIndex);
          if (!ids.length || !(added && added.sections)) window.location.reload();
        });
      })
      .catch(function (e) {
        if (added) { window.location.reload(); return; }
        if (window.console) console.warn('[dg-cart] add', e);
        if (e && e.shop) fail(e.message);
        else fail(e && e.code === 422 ? txt('soldout') : txt('error'));
      });
  }

  /* the button that had focus leaves with its card when the section re-renders: once that happened, put focus on the
     card now at the same place in the same rail, or on the cart title (never steal focus the visitor moved elsewhere) */
  function keepFocus(button, list, index) {
    if (document.activeElement !== button) return;
    var tries = 0;
    (function check() {
      tries += 1;
      if (button.isConnected && document.activeElement === button && tries < 80) { window.setTimeout(check, 50); return; }
      var lost = !document.activeElement || document.activeElement === document.body;
      if (!lost) return;
      var cards = list ? document.querySelectorAll('.dgcu-card[data-list="' + list + '"] .dgcu-add') : [];
      var target = cards.length ? cards[Math.min(Math.max(index, 0), cards.length - 1)] : document.querySelector('.cart-title h1, .cart-title [role="heading"]');
      if (target) {
        if (!target.matches('button, a') && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    })();
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !(form.hasAttribute('data-dg-cu-add') || form.hasAttribute('data-dg-cu-set'))) return;
    if (!window.fetch) return;
    event.preventDefault();
    add(form, event);
  });

  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-dg-cu-prev], [data-dg-cu-next]');
    if (!btn) return;
    var wrap = btn.closest('.dgcu-railwrap');
    var rail = wrap && wrap.querySelector('[data-dg-cu-rail]');
    if (!rail) return;
    var dir = btn.hasAttribute('data-dg-cu-prev') ? -1 : 1;
    var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rail.scrollBy({ left: dir * rail.clientWidth * 0.9, behavior: calm ? 'auto' : 'smooth' });
  });

  /* ---------- recently viewed ---------- */
  function readRecent() {
    try {
      var list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (e) { return []; }
  }
  function fillRecent() {
    var box = document.querySelector('[data-dg-cu-recent]');
    if (!box) return;
    var list = box.querySelector('[data-dg-cu-recent-list]');
    if (!list || list.children.length) return;
    var skip = (box.getAttribute('data-shown') || '') + ((root() && root().getAttribute('data-in')) || '');
    var handles = readRecent().filter(function (h) { return skip.indexOf('|' + h + '|') === -1; });
    if (!handles.length) { box.hidden = true; return; }
    var missing = handles.filter(function (h) { return !(h in cache) && !pending[h]; }).slice(0, 12);
    if (missing.length) {
      missing.forEach(function (h) { pending[h] = true; });
      var base = (root() && root().getAttribute('data-root')) || '/';
      if (base.slice(-1) !== '/') base += '/';
      Promise.all(missing.map(function (h) {
        return fetch(base + 'products/' + encodeURIComponent(h) + '?section_id=dg-cart-card', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .catch(function () { return ''; })
          .then(function (html) {
            var tmp = document.createElement('div');
            tmp.innerHTML = html;
            var item = tmp.querySelector('[data-dg-cu-item]');
            cache[h] = item ? item.outerHTML : '';
            delete pending[h];
          });
      })).then(fillRecent);
      return;
    }
    var html = '';
    var n = 0;
    handles.forEach(function (h) { if (n < 8 && cache[h]) { html += cache[h]; n += 1; } });
    if (!n) { box.hidden = true; return; }
    list.innerHTML = html;
    box.hidden = false;
    var nav = box.querySelector('[data-dg-cu-recent-nav]');
    if (nav) nav.hidden = n < 4;
  }

  var timer = 0;
  function boot() {
    fillRecent();
    var host = document.querySelector('cart-items-component') || document.body;
    /* a cart change re-renders the section (rails included): refill the recent rail from memory */
    new MutationObserver(function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(fillRecent, 40);
    }).observe(host, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
